import { useState, useCallback, useRef, useEffect } from 'react';
import { Box, Typography, IconButton } from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import EditIcon from '@mui/icons-material/Edit';
import { useTranslation } from 'react-i18next';
import { useDrag, useDrop } from 'react-dnd';
import { useNavigate } from 'react-router-dom';
import { LANG_TEXT } from '../../../../constants/language';
import { useUserMenuContext } from '../../../../context/UserMenuContext';
import { ICategoryDetails } from '../../../MenuManagement/menu.types';

interface DraggableCategoryChipProps {
  category: ICategoryDetails;
  index: number;
  moveCategory: (dragIndex: number, hoverIndex: number) => void;
  onDragEnd: () => void;
  selected: boolean;
  onSelect: () => void;
}

const DraggableCategoryChip = ({ category, index, moveCategory, onDragEnd, selected, onSelect }: DraggableCategoryChipProps) => {
  const ref = useRef<HTMLDivElement>(null);

  const [{ isDragging }, drag] = useDrag({
    type: 'category',
    item: { index },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
    end: () => {
      onDragEnd();
    },
  });

  const [, drop] = useDrop({
    accept: 'category',
    hover: (item: { index: number }, monitor) => {
      if (!ref.current) {
        return;
      }

      const dragIndex = item.index;
      const hoverIndex = index;

      if (dragIndex === hoverIndex) {
        return;
      }

      const hoverBoundingRect = ref.current.getBoundingClientRect();
      const hoverMiddleX = (hoverBoundingRect.right - hoverBoundingRect.left) / 2;
      const clientOffset = monitor.getClientOffset();

      if (!clientOffset) {
        return;
      }

      const hoverClientX = clientOffset.x - hoverBoundingRect.left;

      if (dragIndex < hoverIndex && hoverClientX < hoverMiddleX) {
        return;
      }

      if (dragIndex > hoverIndex && hoverClientX > hoverMiddleX) {
        return;
      }

      moveCategory(dragIndex, hoverIndex);
      item.index = hoverIndex;
    },
  });

  drag(drop(ref));

  return (
    <Box
      ref={ref}
      onClick={onSelect}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        px: 2,
        py: 1.5,
        backgroundColor: selected ? 'primary.main' : 'background.paper',
        color: selected ? 'primary.contrastText' : 'text.primary',
        border: 1,
        borderColor: selected ? 'primary.main' : 'divider',
        borderRadius: 2,
        opacity: isDragging ? 0.5 : 1,
        cursor: 'grab',
        '&:active': {
          cursor: 'grabbing',
        },
        transition: 'all 0.2s',
        '&:hover': {
          boxShadow: 2,
        },
        minWidth: 'fit-content',
      }}
    >
      <MenuIcon
        sx={{
          fontSize: 24,
          opacity: 0.7,
        }}
      />
      <Typography
        variant="body1"
        sx={{
          userSelect: 'none',
        }}
      >
        {category.title}
      </Typography>
    </Box>
  );
};

interface CategoryBarProps {
  categories: ICategoryDetails[];
  onCategoryOrderChange: (categories: ICategoryDetails[]) => void;
}

export default function CategoryBar({ categories: propCategories, onCategoryOrderChange }: CategoryBarProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { activeCategoryId, setActiveCategoryId } = useUserMenuContext();
  const [categories, setCategories] = useState<ICategoryDetails[]>([]);

  useEffect(() => {
    setCategories(propCategories);
  }, [propCategories]);

  const moveCategory = useCallback((dragIndex: number, hoverIndex: number) => {
    setCategories((prevCategories) => {
      const newCategories = [...prevCategories];
      const [draggedItem] = newCategories.splice(dragIndex, 1);
      newCategories.splice(hoverIndex, 0, draggedItem);
      const reindexed = newCategories.map((cat, idx) => ({ ...cat, order: idx }));
      return reindexed;
    });
  }, []);

  const handleDragEnd = useCallback(() => {
    onCategoryOrderChange(categories);
  }, [categories, onCategoryOrderChange]);

  return (
    <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5, minWidth: 'fit-content' }}>
        <Typography variant="body2" color="text.secondary" fontWeight={600}>
          {t(LANG_TEXT.CATEGORIES)}
        </Typography>
        <IconButton size="small" onClick={() => navigate('/categories_management')}>
          <EditIcon fontSize="small" />
        </IconButton>
      </Box>

      <Box
        sx={{
          display: 'flex',
          gap: 2,
          overflowX: 'auto',
          pb: 1,
          width: '100%',
          '&::-webkit-scrollbar': { height: 8 },
          '&::-webkit-scrollbar-thumb': { backgroundColor: '#bdbdbd', borderRadius: 4 },
          '&::-webkit-scrollbar-track': { backgroundColor: 'transparent' },
        }}
      >
        {categories.map((category, index) => (
          <DraggableCategoryChip
            key={category.id}
            category={category}
            index={index}
            moveCategory={moveCategory}
            onDragEnd={handleDragEnd}
            selected={category.id === activeCategoryId}
            onSelect={() => setActiveCategoryId(category.id)}
          />
        ))}
      </Box>
    </Box>
  );
}
